import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useApolloClient } from '@apollo/client'
import { useAxios } from '@/axios/config'
import { useSnackBar } from '@/hooks/useSnackBar'
import { PAYMENT_METHODS_QUERY_KEY } from '@/pages/AdminPanel/Payments/hooks/useFetchPaymentMethod'
import { ApiResponse } from '@/pages/AdminPanel/Payments/types'

// Minimal shape of the UsersMe refetch result. We avoid importing
// the generated `UsersMeQuery` type because that pulls the entire
// `@/gql/systemApi` module into test bundles.
interface UsersMeRefetchData {
  users_me?: {
    current_organization?: Array<{
      organization_id?: { payment_status?: string | null } | null
    } | null> | null
  } | null
}

const MAX_ATTEMPTS = 5
const DELAY_MS = 1500
const MEMBERS_PRICES_QUERY_KEY = 'membersPrices'
const PAYMENT_ADD_PARAM = 'payment_add'

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms)
  })

export const usePaymentReturnHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const apolloClient = useApolloClient()
  const { showAlert } = useSnackBar()

  const [shouldProcessReturn] = useState(
    () => searchParams.get('from') === PAYMENT_ADD_PARAM
  )

  const [, fetchPaymentMethod] = useAxios<ApiResponse>({
    url: '/v1/admin/payments',
    method: 'GET',
  })
  const fetchPaymentMethodRef = useRef(fetchPaymentMethod)
  fetchPaymentMethodRef.current = fetchPaymentMethod

  const stripFromParamRef = useRef<() => void>(() => {})
  stripFromParamRef.current = () => {
    setSearchParams(
      prev => {
        prev.delete('from')
        return prev
      },
      { replace: true }
    )
  }

  useEffect(() => {
    if (!shouldProcessReturn) return undefined

    stripFromParamRef.current()

    let cancelled = false

    const attempt = async (remaining: number): Promise<void> => {
      if (cancelled) return

      let isPaymentMethodCurrent = false
      let isPaymentStatusActive = false

      try {
        // Refresh the REST payment-method cache and the Apollo UsersMe cache in
        // parallel. UsersMe drives the `payment_status` gates across the app.
        const [paymentMethodResult, usersMeResults] = await Promise.all([
          fetchPaymentMethodRef.current(),
          apolloClient.refetchQueries({ include: ['UsersMe'] }),
        ])
        if (cancelled) return

        const paymentMethodData = paymentMethodResult.data
        queryClient.setQueryData(PAYMENT_METHODS_QUERY_KEY, paymentMethodData)
        queryClient.invalidateQueries(MEMBERS_PRICES_QUERY_KEY)

        isPaymentMethodCurrent = Boolean(
          paymentMethodData?.paymentMethod?.currentCard?.isCurrent
        )

        const usersMeData = usersMeResults?.[0]?.data as
          | UsersMeRefetchData
          | undefined
        const paymentStatus =
          usersMeData?.users_me?.current_organization?.[0]?.organization_id
            ?.payment_status
        isPaymentStatusActive = paymentStatus === 'active'

        if (isPaymentMethodCurrent && isPaymentStatusActive) {
          showAlert({
            message: 'Payment method added successfully',
            severity: 'success',
          })
          return
        }
      } catch {
        // Transient error — fall through to retry
      }

      if (cancelled) return

      if (remaining <= 1) {
        // Card was saved at Stripe but the BE hasn't flipped payment_status
        // to 'active' yet (webhook lag). Don't loop the user back to Stripe —
        // tell them it's still processing.
        if (isPaymentMethodCurrent && !isPaymentStatusActive) {
          showAlert({
            message:
              "Your card was saved. We're still processing the payment method — refresh in a moment.",
            severity: 'warning',
          })
        } else {
          showAlert({
            message:
              'Payment setup may not have completed. Check your payment settings.',
            severity: 'warning',
          })
        }
        return
      }

      await sleep(DELAY_MS)
      await attempt(remaining - 1)
    }

    attempt(MAX_ATTEMPTS)

    return () => {
      cancelled = true
    }
  }, [shouldProcessReturn, queryClient, apolloClient, showAlert])
}
